import {
  DecisionAction,
  DecisionActionCompact,
  DecisionActivity,
  DecisionActivityActor,
  DecisionActivityImpact,
  DecisionDetailItem,
  DecisionDocumentRef,
  DecisionEvidence,
  DecisionListItem,
  DecisionMissingInformation,
  DecisionOwner,
  DecisionQuery,
  DecisionSourceTrace,
  DecisionSummary,
  HomeIaActionInputProperty,
  HomeIaActionInputSchema,
} from "../../../models/home";
import {
  HomeActivityActorResponse,
  HomeActivityImpactResponse,
  HomeActivityNarrativeResponse,
  HomeDecisionDocumentRefResponse,
  HomeDecisionFiltersResponse,
  HomeDecisionItemResponse,
  HomeDecisionListItemResponse,
  HomeDecisionOwnerResponse,
  HomeDecisionSourceTraceResponse,
  HomeDecisionSummaryResponse,
  HomeEvidenceResponse,
  HomeIaActionInputPropertyResponse,
  HomeIaActionInputSchemaResponse,
  HomeMissingInformationResponse,
  HomeNextBestActionCompactResponse,
  HomeNextBestActionResponse,
} from "../../../models/home/dtos";
import {
  toOptionalState,
  toConfidenceCompactViewModel,
  toConfidenceViewModel,
  toOptionalExpectedState,
  toOptionalActionHttpMethod,
  toPriorityCompactViewModel,
  toPriorityViewModel,
  toRiskCompactViewModel,
  toRiskViewModel,
  toState
} from "./decision.adapter.status-mappers";

export function toEvidenceViewModel(dto: HomeEvidenceResponse): DecisionEvidence {
    return {
      evidenceId: dto.evidenceId ?? undefined,
      type: dto.type ?? undefined,
      label: dto.label ?? undefined,
      value: dto.value ?? undefined,
      documentId: dto.documentId ?? undefined,
      classificationId: dto.classificationId ?? undefined,
      confidence: dto.confidence ?? 0,
      source: dto.source ?? undefined,
      occurredAt: dto.occurredAt ?? undefined
    };
  }

export function toMissingInformationViewModel(dto: HomeMissingInformationResponse): DecisionMissingInformation {
    return {
      key: dto.key ?? undefined,
      label: dto.label ?? undefined,
      reason: dto.reason ?? undefined,
      requiredForAction: dto.requiredForAction ?? false,
      severity: dto.severity ?? undefined,
      resolutionLocation: dto.resolutionLocation ?? undefined,
      resolutionHint: dto.resolutionHint ?? undefined,
      suggestedUserInput: dto.suggestedUserInput ?? undefined
    };
  }

export function toSourceTraceViewModel(
    dto?: HomeDecisionSourceTraceResponse | null
  ): DecisionSourceTrace | undefined {
    if (!dto) {
      return undefined;
    }

    return {
      traceId: dto.traceId ?? undefined,
      actionSource: dto.actionSource ?? undefined,
      actionSourceDetail: dto.actionSourceDetail ?? undefined,
      confidenceSource: dto.confidenceSource ?? undefined,
      confidenceSourceDetail: dto.confidenceSourceDetail ?? undefined,
      explanationSource: dto.explanationSource ?? undefined,
      explanationSourceDetail: dto.explanationSourceDetail ?? undefined,
      catalogHealthAtEvaluation: dto.catalogHealthAtEvaluation ?? undefined,
      evaluatorVersion: dto.evaluatorVersion ?? undefined,
      rulesVersion: dto.rulesVersion ?? undefined
    };
  }

export function toActivityViewModel(dto: HomeActivityNarrativeResponse): DecisionActivity {
    return {
      eventId: dto.eventId ?? "",
      decisionId: dto.decisionId ?? "",
      documentIds: dto.documentIds ?? [],
      type: dto.type ?? "",
      title: dto.title ?? "",
      narrative: dto.narrative ?? "",
      actor: toActivityActorViewModel(dto.actor),
      source: dto.source ?? undefined,
      occurredAt: dto.occurredAt ?? "",
      impact: toActivityImpactViewModel(dto.impact),
      tracePayload: dto.tracePayload ?? undefined
    };
  }

export function toActivityActorViewModel(
    dto?: HomeActivityActorResponse | null
  ): DecisionActivityActor | undefined {
    if (!dto?.userId && !dto?.displayName && !dto?.kind) {
      return undefined;
    }

    return {
      userId: dto.userId ?? undefined,
      displayName: dto.displayName ?? undefined,
      kind: dto.kind ?? undefined
    };
  }

export function toActivityImpactViewModel(
    dto?: HomeActivityImpactResponse | null
  ): DecisionActivityImpact | undefined {
    if (!dto?.previousState && !dto?.newState && !dto?.summary) {
      return undefined;
    }

    return {
      previousState: toOptionalState(dto.previousState),
      newState: toOptionalState(dto.newState),
      summary: dto.summary ?? undefined
    };
  }
